import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Hammer, Wrench, Zap as ZapIcon, PaintBucket,
  Clock, FolderX, MonitorX, EyeOff, Puzzle,
  ArrowRight, Check, ArrowUpRight,
  Briefcase, Store, User, Truck
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 }
};

const clients = [
  { icon: Hammer, label: "Artisans du bâtiment", desc: "Maçons, couvreurs, carreleurs, peintres" },
  { icon: ZapIcon, label: "Électriciens", desc: "Installations, dépannages, rénovations" },
  { icon: Wrench, label: "Plombiers", desc: "Plomberie, chauffage, climatisation" },
  { icon: PaintBucket, label: "Menuisiers & charpentiers", desc: "Bois, agencement, construction" },
  { icon: Store, label: "Commerces locaux", desc: "Boutiques, restaurants, services" },
  { icon: User, label: "Freelances", desc: "Consultants, coachs, formateurs" },
  { icon: Briefcase, label: "Entrepreneurs", desc: "Créateurs d'entreprise, dirigeants de TPE" },
  { icon: Truck, label: "Services aux entreprises", desc: "Transport, nettoyage, maintenance" },
];

const challenges = [
  { icon: Clock, title: "Manque de temps", desc: "Débordés par l'opérationnel, peu de temps pour s'organiser et se développer." },
  { icon: FolderX, title: "Surcharge administrative", desc: "Devis, factures, relances : des tâches chronophages qui freinent la croissance." },
  { icon: MonitorX, title: "Outils désorganisés", desc: "Fichiers éparpillés, pas de CRM, aucun suivi structuré de l'activité." },
  { icon: EyeOff, title: "Manque de visibilité en ligne", desc: "Pas de site, pas de présence digitale, difficulté à attirer de nouveaux clients." },
  { icon: Puzzle, title: "Difficulté à se structurer", desc: "Pas de processus clairs, tout repose sur la mémoire et le papier." },
];

const solutions = [
  "Un site internet professionnel qui génère des demandes",
  "Des outils de gestion adaptés à votre métier",
  "Des automatisations qui éliminent les tâches répétitives",
  "Un diagnostic stratégique pour identifier vos priorités",
  "Un accompagnement sur mesure, à votre rythme",
];

export default function ClientsPage() {
  return (
    <div data-testid="clients-page">
      {/* Header */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#59C3A6]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div {...fadeUp}>
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Nos clients</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 mb-6 text-white">
              Nous aidons ceux qui{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#59C3A6] to-[#4AB095]">
                construisent
              </span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              Finovéo accompagne les artisans, les petites entreprises et les entrepreneurs qui veulent structurer et moderniser leur activité.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Client types */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">Nos clients types</h2>
            <p className="text-slate-400 text-base max-w-xl">Des professionnels passionnés par leur métier, qui cherchent à gagner en efficacité et en visibilité.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {clients.map((client, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`client-type-${i}`}
                className="group p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5 hover:border-[#59C3A6]/20 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center mb-4 group-hover:bg-[#59C3A6]/20 transition-colors">
                  <client.icon size={20} className="text-[#59C3A6]" />
                </div>
                <h3 className="font-heading text-base font-semibold text-white mb-1">{client.label}</h3>
                <p className="text-slate-400 text-sm">{client.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#071428]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: challenges */}
            <motion.div {...fadeUp}>
              <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Les défis</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mt-3 mb-8">
                Des problèmes que nous comprenons
              </h2>
              <div className="space-y-5">
                {challenges.map((challenge, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-xl bg-[#0B1F3B]/60 border border-white/5">
                    <div className="w-10 h-10 rounded-lg bg-[#59C3A6]/10 flex items-center justify-center shrink-0">
                      <challenge.icon size={18} className="text-[#59C3A6]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-white mb-1">{challenge.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{challenge.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: solutions */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Nos réponses</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mt-3 mb-8">
                Comment Finovéo vous aide
              </h2>
              <div className="bg-[#112A4F]/30 rounded-2xl border border-[#59C3A6]/10 p-8">
                <ul className="space-y-4">
                  {solutions.map((sol, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-[#59C3A6] mt-0.5 shrink-0" />
                      <span className="text-slate-300 text-base">{sol}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/5">
                  <p className="text-slate-400 text-sm mb-5 leading-relaxed">
                    L'objectif : vous donner les outils et la stratégie pour vous concentrer sur ce que vous faites de mieux — votre métier.
                  </p>
                  <Link to="/contact">
                    <Button
                      data-testid="clients-cta"
                      className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(89,195,166,0.25)]"
                    >
                      Demander un diagnostic <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO section */}
      <section className="py-16 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p {...fadeUp} className="text-slate-500 text-sm leading-relaxed">
            Finovéo Conseil accompagne les artisans et petites entreprises dans leur digitalisation. Nos solutions d'automatisation pour artisans, d'outils numériques pour PME et d'optimisation de l'organisation permettent aux entrepreneurs de gagner en efficacité grâce à l'intelligence artificielle pour entreprises.
          </motion.p>
        </div>
      </section>
    </div>
  );
}
